import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import apiService from '../../services/ApiService';
import { useAuth } from '../../contexts/AuthContext';
import { useEventPublicPaths, useEventRouteParams } from '../../hooks/useEventRouteParams';
import { redirectToLogin, shouldPromptForLogin } from '../../utils/authRedirect';
import type { PublicEventData, SpectatorTicketType } from '../../types/event';
import { formatPriceCents, eventOffersSpectatorTickets } from '../../utils/registrationStatus';
import PageLoader from '../ui/PageLoader';
import Footer from '../Footer';
import Button from '../ui/Button';
import PageBackLink from '../ui/PageBackLink';

const SpectatorTicketsPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const { apiQuery } = useEventRouteParams();

  const [eventData, setEventData] = useState<PublicEventData | null>(null);
  const paths = useEventPublicPaths(eventData?.event ?? null);
  const [ticketTypes, setTicketTypes] = useState<SpectatorTicketType[]>([]);
  const [selectedType, setSelectedType] = useState<number | ''>('');
  const [quantity, setQuantity] = useState(1);
  const [holderName, setHolderName] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const ticketsOffered = eventOffersSpectatorTickets(
    eventData?.event.entry_mode,
    eventData?.feature_flags?.spectator_tickets_enabled,
  );

  useEffect(() => {
    if (shouldPromptForLogin(isAuthenticated, authLoading, location)) {
      redirectToLogin(navigate, location.pathname, { replace: true });
    }
  }, [authLoading, isAuthenticated, navigate, location]);

  useEffect(() => {
    if (!apiQuery || !isAuthenticated) return;
    (async () => {
      try {
        setLoading(true);
        setError(null);
        const evt = await apiService.getPublicEventBySlug(apiQuery);
        setEventData(evt);
        if (eventOffersSpectatorTickets(evt.event.entry_mode, evt.feature_flags?.spectator_tickets_enabled)) {
          const types = await apiService.listTicketTypes(evt.event.id);
          setTicketTypes(types);
          if (types.length === 1) setSelectedType(types[0].id);
        }
      } catch (err: any) {
        setError(err.message || 'Failed to load tickets');
      } finally {
        setLoading(false);
      }
    })();
  }, [apiQuery, isAuthenticated]);

  const selectedTicket = ticketTypes.find((t) => t.id === selectedType);

  const handlePurchase = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!eventData || selectedType === '') {
      setError('Select a ticket type');
      return;
    }
    try {
      setSubmitting(true);
      setError(null);
      const result = await apiService.createSpectatorCheckout({
        event_id: eventData.event.id,
        ticket_type_id: Number(selectedType),
        quantity,
        holder_name: holderName.trim() || undefined,
      });
      if (result.free) {
        if (result.return_url && /^https?:\/\//i.test(result.return_url)) {
          window.location.href = result.return_url;
          return;
        }
        const orderId = result.order?.id ? String(result.order.id) : '';
        navigate(`${paths.ticketsReturn}?order_id=${encodeURIComponent(orderId)}&free=1`);
        return;
      }
      if (!result.checkout_url) {
        throw new Error('Checkout URL missing');
      }
      window.location.href = result.checkout_url;
    } catch (err: any) {
      setError(err.message || 'Could not start checkout');
    } finally {
      setSubmitting(false);
    }
  };

  if (!authLoading && !isAuthenticated) {
    return (
      <>
        <PageLoader message="Loading tickets…" />
        <Footer />
      </>
    );
  }

  if (authLoading || loading) {
    return (
      <>
        <PageLoader message="Loading tickets…" />
        <Footer />
      </>
    );
  }

  if (!eventData) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] text-white flex flex-col items-center justify-center px-4">
        <p className="text-muted text-sm mb-4 text-center max-w-md">
          {error || 'Event not found'}
        </p>
        <Link to="/events" className="btn-primary min-h-[44px] inline-flex">
          Browse events
        </Link>
        <Footer />
      </div>
    );
  }

  if (!ticketsOffered) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] text-white flex flex-col items-center justify-center px-4">
        <p className="text-muted text-sm mb-4 text-center max-w-md">
          This event is free to attend. Spectator tickets are not sold.
        </p>
        <Link to={paths.base} className="btn-primary min-h-[44px] inline-flex">
          Back to event
        </Link>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white flex flex-col">
      <div className="flex-1 max-w-lg mx-auto w-full px-4 py-8">
        <PageBackLink label="Back to Event" to={paths.base} className="mb-4" />
        <p className="kicker mb-2">Spectator tickets</p>
        <h1 className="heading-display mb-1">{eventData.event.title}</h1>
        <p className="text-muted text-sm mb-6 leading-relaxed">
          Purchase admission tickets for spectators. Vehicle entrants register separately.
        </p>

        {error ? (
          <div className="alert-error mb-4 text-sm" role="alert">
            {error}
          </div>
        ) : null}

        {ticketTypes.length === 0 ? (
          <div className="card p-6 text-center">
            <p className="text-muted text-sm">Ticket types are not on sale yet. Check back soon.</p>
          </div>
        ) : (
          <form onSubmit={(e) => void handlePurchase(e)} className="space-y-5">
            <div>
              <label htmlFor="ticket-type" className="input-label">
                Ticket type <span className="text-red-400">*</span>
              </label>
              <select
                id="ticket-type"
                value={selectedType}
                onChange={(e) => setSelectedType(e.target.value ? Number(e.target.value) : '')}
                className="input"
                required
              >
                <option value="">Select a ticket…</option>
                {ticketTypes.map((t) => (
                  <option key={t.id} value={t.id} disabled={t.remaining === 0}>
                    {t.name} · {formatPriceCents(t.price_cents, t.currency)}
                    {t.remaining != null ? ` · ${t.remaining} left` : ''}
                  </option>
                ))}
              </select>
              {selectedTicket?.description ? (
                <p className="text-xs text-gray-500 mt-1">{selectedTicket.description}</p>
              ) : null}
            </div>

            <div>
              <label htmlFor="ticket-qty" className="input-label">
                Quantity
              </label>
              <input
                id="ticket-qty"
                type="number"
                min={1}
                max={selectedTicket?.per_order_limit || 10}
                value={quantity}
                onChange={(e) => setQuantity(Math.max(1, Number(e.target.value)))}
                className="input"
              />
            </div>

            <div>
              <label htmlFor="holder-name" className="input-label">
                Name on ticket <span className="text-meta font-normal">(optional)</span>
              </label>
              <input
                id="holder-name"
                type="text"
                value={holderName}
                onChange={(e) => setHolderName(e.target.value)}
                className="input"
                maxLength={120}
              />
            </div>

            {selectedTicket ? (
              <p className="text-sm text-gray-400">
                Total: {formatPriceCents(selectedTicket.price_cents * quantity, selectedTicket.currency)}
              </p>
            ) : null}

            <Button type="submit" variant="primary" loading={submitting} fullWidth className="min-h-[44px]">
              Continue to checkout
            </Button>
          </form>
        )}
      </div>
      <Footer />
    </div>
  );
};

export default SpectatorTicketsPage;
